import { useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "./useCart";
import {
  addToCart,
  updateCart,
  getCart,
  removeFromCart,
} from "../services/cart.service";
import { toCartRequestItems } from "../services/cartOwnership.service";
import { toIdString } from "@horseed/shared/ids";

function getServerItems(serverCart) {
  return Array.isArray(serverCart?.items) ? serverCart.items : [];
}

// Keyed by GUID string — Number() would collapse every id to NaN, which
// collides into a single Map key.
function getServerQuantityById(serverItems) {
  return new Map(
    serverItems
      .filter((item) => item?.itemId != null)
      .map((item) => [toIdString(item.itemId), Number(item.quantity) || 0]),
  );
}

async function syncServerCartToLocalCart(userId, localCart, options = {}) {
  const removeMissing = options.removeMissing === true;
  const desiredItems = toCartRequestItems(localCart);
  const serverCart = await getCart(userId);
  const serverItems = getServerItems(serverCart);
  const serverQuantityById = getServerQuantityById(serverItems);
  const desiredIds = new Set(desiredItems.map((item) => toIdString(item.itemId)));

  const missingItems = desiredItems
    .map((item) => {
      const serverQuantity = serverQuantityById.get(toIdString(item.itemId)) || 0;
      return {
        itemId: item.itemId,
        quantity: Math.max(0, item.quantity - serverQuantity),
      };
    })
    .filter((item) => item.quantity > 0);

  const removedItems = removeMissing
    ? serverItems
        .filter((item) => item?.itemId != null && !desiredIds.has(toIdString(item.itemId)))
        .map((item) => ({
          itemId: toIdString(item.itemId),
          quantity: Number(item.quantity) || 1,
        }))
    : [];

  if (missingItems.length > 0) {
    await addToCart(userId, missingItems);
  }

  if (removedItems.length > 0) {
    await removeFromCart(userId, removedItems);
  }

  if (desiredItems.length > 0) {
    await updateCart(userId, desiredItems);
  }

  return await getCart(userId);
}

/**
 * Lightweight server-cart synchronizer.
 * - On login: push local items to the server and pull server cart.
 * - On subsequent local changes: debounced PUT to /Cart/update.
 *
 * The localStorage cart remains the source of truth for guests; once logged
 * in, the server cart is mirrored so other devices see the same items.
 */
export function useCartSync() {
  const { user, isAuthenticated } = useAuth();
  const { cart, addItem, updateItem } = useCart();
  const hasMerged = useRef(false);
  const lastSerialized = useRef("");
  const debounceRef = useRef(null);

  const userId = user?.id;

  useEffect(() => {
    if (!isAuthenticated || !userId) {
      hasMerged.current = false;
      return undefined;
    }

    if (hasMerged.current) return undefined;

    let cancelled = false;

    (async () => {
      try {
        const serverCart = await syncServerCartToLocalCart(userId, cart, {
          removeMissing: false,
        });
        if (cancelled) return;

        const serverItems = getServerItems(serverCart);
        const localById = new Map(
          (cart || []).map((i) => [toIdString(i.ProductId), i]),
        );

        serverItems.forEach((si) => {
          if (si?.itemId == null) return;

          const id = toIdString(si.itemId);
          const qty = Number(si.quantity) || 1;
          const price = Number(si.price) || 0;

          if (localById.has(id)) {
            updateItem(id, { Quantity: qty });
            return;
          }

          addItem({
            ProductId: id,
            Name: si.name || si.itemName || `Item ${id}`,
            Image: si.image || "",
            Price: price,
            Quantity: qty,
          });
        });

        hasMerged.current = true;
      } catch (_) {
        /* keep local cart */
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, userId]);

  useEffect(() => {
    if (!isAuthenticated || !userId || !hasMerged.current) return undefined;

    const serialized = JSON.stringify(
      (cart || []).map((i) => [i.ProductId, i.Quantity]),
    );

    if (serialized === lastSerialized.current) return undefined;
    lastSerialized.current = serialized;

    if (debounceRef.current) window.clearTimeout(debounceRef.current);

    debounceRef.current = window.setTimeout(() => {
      syncServerCartToLocalCart(userId, cart, { removeMissing: true }).catch(() => {
        /* local cart wins */
      });
    }, 800);

    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [cart, isAuthenticated, userId]);
}
